import { extractContentType } from '@shared/http/utils/extract-content-type';
import { getHttpVersion } from '@shared/http/utils/get-http-version';
import { postEventMessage, sendErrorToSentry } from '@shared/sentry';

export function fetchInjection(): void {
  const originalFetch = window.fetch;

  window.fetch = async (input, init) => {
    const startDateTime = new Date();
    // has to be cloned before using it to fetch the response
    const clonedInput = input instanceof Request ? input.clone() : input;
    const response = await originalFetch(input, init);
    try {
      const responseCopy = response.clone();
      const responseBody = await responseCopy.text();

      const durationInMs = new Date().getTime() - startDateTime.getTime();
      const method = getMethod(clonedInput, init);
      const requestBody = await getBody(clonedInput, init);
      const responseContentType = extractContentType(responseCopy.headers.get('content-type'));

      postEventMessage({
        pageUrl: window.location.href,
        request: {
          body: requestBody,
          durationInMs,
          httpVersion: getHttpVersion(),
          method,
          startDateTime: startDateTime.toISOString(),
          url: getUrlStringFromInput(clonedInput),
        },
        response: {
          body: responseBody === '' ? undefined : responseBody,
          contentType: responseContentType ? responseContentType : undefined,
          redirectUrl: responseCopy.redirected ? responseCopy.url : undefined,
          status: responseCopy.status,
        },
      });
    } catch (e) {
      sendErrorToSentry({ error: e });
    }
    return response;
  };
};

function getMethod(input: RequestInfo | URL, init: RequestInit | undefined): string {
  if (init?.method) {
    return init.method;
  }
  if (input instanceof Request) {
    return input.method;
  }
  return 'GET';
}

async function getBody(
  input: RequestInfo | URL,
  init: RequestInit | undefined
): Promise<string | ReadableStream<any> | ArrayBufferView | ArrayBuffer | FormData | undefined> {
  if (init?.body) {
    if (init.body instanceof Blob) {
      return await init.body.text();
    }
    return init.body as string;
  }
  if (input instanceof Request) {
    return await input.text();
  }
  return undefined;
}

function getUrlStringFromInput(input: RequestInfo | URL): string {
  if (input instanceof URL) {
    return input.href;
  }
  if (input instanceof Request) {
    return input.url;
  }
  return input;
}
